import {
	Alert,
	Box,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
} from "@mui/material";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import type { MessageDocument } from "../../../api/documents/message-documents";
import { addEmailToQueue } from "../../../api/messaging/email";
import type { PersonEmail } from "../../../api/personemail/person-email";
import { getEmailSignature } from "../../../api/user";
import { EmojiPickerButton } from "../../shared/EmojiPickerButton";
import type { ActivityPanelHandle, ActivityPanelProps } from "./panel-types";

interface EmailPanelProps extends ActivityPanelProps {
	personId: number;
	userId: number;
	personEmails: PersonEmail[];
	/** Owned by the dialog, which renders the attach control under the panel. */
	documents?: MessageDocument[];
}

export const EmailPanel = forwardRef<ActivityPanelHandle, EmailPanelProps>(
	({ personId, userId, personEmails, documents = [], onCanSaveChange }, ref) => {
		const [content, setContent] = useState("");
		const [subject, setSubject] = useState("");
		const [selectedEmailId, setSelectedEmailId] = useState<number | "">(
			personEmails.length > 0 ? (personEmails[0].id ?? "") : ""
		);
		const [isLoadingSignature, setIsLoadingSignature] = useState(true);
		const signatureInitialized = useRef(false);

		const cursorRef = useRef({ start: 0, end: 0 });
		const inputRef = useRef<HTMLTextAreaElement>(null);

		const hasEmails = personEmails.length > 0;

		const canSave =
			hasEmails &&
			!isLoadingSignature &&
			content.trim().length > 0 &&
			subject.trim().length > 0 &&
			selectedEmailId !== "";

		useEffect(() => {
			onCanSaveChange(canSave);
		}, [canSave, onCanSaveChange]);

		useImperativeHandle(ref, () => ({
			save: async () => {
				await addEmailToQueue({
					from: userId,
					to: personId,
					text: content,
					sendImmediately: true,
					subject,
					personEmailId: selectedEmailId !== "" ? selectedEmailId : undefined,
					documentIds: documents.map((doc) => doc.id),
				});
			},
		}));

		// Fetch signature and pre-populate content once on mount
		useEffect(() => {
			if (signatureInitialized.current) return;
			signatureInitialized.current = true;
			getEmailSignature()
				.then((res) => {
					if (res.signature_text) {
						setContent(`\n\n${res.signature_text}`);
					}
				})
				.finally(() => setIsLoadingSignature(false));
		}, []);

		const handleEmojiSelect = (emoji: string) => {
			const start = cursorRef.current.start;
			const end = cursorRef.current.end;
			const newContent =
				content.substring(0, start) + emoji + content.substring(end);
			setContent(newContent);

			const newPos = start + emoji.length;
			cursorRef.current = { start: newPos, end: newPos };

			setTimeout(() => {
				if (inputRef.current) {
					inputRef.current.focus();
					inputRef.current.setSelectionRange(newPos, newPos);
				}
			}, 0);
		};

		const handleSelect = () => {
			if (inputRef.current) {
				cursorRef.current = {
					start: inputRef.current.selectionStart ?? 0,
					end: inputRef.current.selectionEnd ?? 0,
				};
			}
		};

		if (!hasEmails) {
			return (
				<Alert severity="warning" data-component="no-email-alert">
					No email address available for this person. Add an email address in
					the Contact tab first.
				</Alert>
			);
		}

		if (isLoadingSignature) {
			return (
				<Box
					sx={{ display: "flex", justifyContent: "center", py: 4 }}
					data-component="email-loading"
				>
					<CircularProgress size={28} />
				</Box>
			);
		}

		return (
			<Stack spacing={2}>
				<FormControl fullWidth data-component="email-recipient-control">
					<InputLabel>To</InputLabel>
					<Select
						value={selectedEmailId}
						label="To"
						onChange={(e) => setSelectedEmailId(e.target.value as number)}
						data-component="email-recipient-select"
					>
						{personEmails.map((email) => (
							<MenuItem key={email.id} value={email.id}>
								{email.email}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<TextField
					label="Subject"
					value={subject}
					onChange={(e) => setSubject(e.target.value)}
					fullWidth
					data-component="email-subject-input"
				/>
				<Box data-component="email-content-wrapper">
					<TextField
						label="Message"
						value={content}
						onChange={(e) => setContent(e.target.value)}
						onSelect={handleSelect}
						inputRef={inputRef}
						multiline
						minRows={8}
						fullWidth
						placeholder="Write your email message..."
						data-component="email-content-input"
					/>
					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-end",
							mt: 0.5,
						}}
					>
						<EmojiPickerButton onEmojiSelect={handleEmojiSelect} />
					</Box>
				</Box>
			</Stack>
		);
	}
);

EmailPanel.displayName = "EmailPanel";
